import { Dialog } from "@base-ui/react/dialog";
import {
  getLockerverseMinimumCustomAmountCents,
  type LockerverseCatalog,
  type LockerverseProduct,
} from "@lockerverse/sdk/checkout";
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useState,
} from "react";
import { getAccentContrast, normalizeAccentColor } from "./accent-contrast.ts";
import { LockerversePayment } from "./lockerverse-payment.tsx";
import { preloadLockerversePayment } from "./payment-form.tsx";
import type {
  LockerverseDonateLauncherProps,
  LockerversePaymentProps,
  LockerversePaymentSelectionItem,
  LockerversePaymentStyle,
} from "./types.ts";
import type { UseLockerverseWidgetResult } from "./use-lockerverse-widget.ts";

const usd = new Intl.NumberFormat("en-US", {
  currency: "USD",
  style: "currency",
});
const decimalAmountPattern = /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/;
const suggestedDonationAmountsCents = [1000, 2500, 5000, 10_000];

function parseAmountCents(value: string) {
  if (!decimalAmountPattern.test(value)) {
    return null;
  }
  const amountCents = Math.round(Number(value) * 100);
  return Number.isSafeInteger(amountCents) && amountCents > 0
    ? amountCents
    : null;
}

function formatProductPrice(
  amountCents: number,
  paymentOption: "one-time" | "monthly" | "annually"
) {
  let cadence = "";
  if (paymentOption === "monthly") {
    cadence = "/month";
  }
  if (paymentOption === "annually") {
    cadence = "/year";
  }
  return `${usd.format(amountCents / 100)}${cadence}`;
}

type LauncherProductControlProps = {
  customAmounts: Record<string, string>;
  product: LockerverseProduct;
  quantities: Record<string, number>;
  recurringProductId: string | null;
  setCustomAmounts: Dispatch<SetStateAction<Record<string, string>>>;
  setQuantities: Dispatch<SetStateAction<Record<string, number>>>;
  setRecurringProductId: Dispatch<SetStateAction<string | null>>;
};

function LauncherProductControl({
  customAmounts,
  product,
  quantities,
  recurringProductId,
  setCustomAmounts,
  setQuantities,
  setRecurringProductId,
}: LauncherProductControlProps) {
  const quantity = quantities[product.id] ?? 0;
  if (product.pricingMode === "fixed" && product.paymentOption === "one-time") {
    const minimum = product.minimumQuantity ?? 1;
    const maximum = Math.min(
      product.maximumQuantity ?? Number.POSITIVE_INFINITY,
      product.inventoryQuantity ?? Number.POSITIVE_INFINITY
    );
    return (
      <span className="lockerverse-launcher__quantity">
        <button
          aria-label={`Decrease ${product.name} quantity`}
          disabled={quantity === 0}
          onClick={() => {
            setRecurringProductId(null);
            setQuantities((current) => {
              const currentQuantity = current[product.id] ?? 0;
              return {
                ...current,
                [product.id]:
                  currentQuantity <= minimum ? 0 : currentQuantity - 1,
              };
            });
          }}
          type="button"
        >
          −
        </button>
        <output aria-label={`${product.name} quantity`}>{quantity}</output>
        <button
          aria-label={`Increase ${product.name} quantity`}
          disabled={maximum < minimum || quantity >= maximum}
          onClick={() => {
            setRecurringProductId(null);
            setQuantities((current) => {
              const currentQuantity = current[product.id] ?? 0;
              return {
                ...current,
                [product.id]:
                  currentQuantity === 0 ? minimum : currentQuantity + 1,
              };
            });
          }}
          type="button"
        >
          +
        </button>
      </span>
    );
  }

  if (
    product.pricingMode === "custom" &&
    product.paymentOption === "one-time"
  ) {
    return (
      <span className="lockerverse-launcher__custom-amount">
        <span aria-hidden="true">$</span>
        <input
          aria-label={`${product.name} amount`}
          min={getLockerverseMinimumCustomAmountCents(product) / 100}
          name={`amount-${product.slug}`}
          onChange={(event) => {
            setRecurringProductId(null);
            setCustomAmounts((current) => ({
              ...current,
              [product.id]: event.target.value,
            }));
          }}
          placeholder="0.00"
          step="0.01"
          type="number"
          value={customAmounts[product.id] ?? ""}
        />
        {product.minimumAmountCents === null ? null : (
          <small>Minimum {usd.format(product.minimumAmountCents / 100)}</small>
        )}
      </span>
    );
  }

  return (
    <button
      aria-label={`Select ${product.name}`}
      aria-pressed={recurringProductId === product.id}
      className="lockerverse-launcher__select"
      onClick={() => {
        setQuantities({});
        setCustomAmounts({});
        setRecurringProductId((current) =>
          current === product.id ? null : product.id
        );
      }}
      type="button"
    >
      Select
    </button>
  );
}

type LauncherProductOptionProps = LauncherProductControlProps & {
  variant?: "default" | "donation";
};

function LauncherProductOption({
  variant = "default",
  ...props
}: LauncherProductOptionProps) {
  const { customAmounts, product, quantities, recurringProductId } = props;
  const customAmount = parseAmountCents(customAmounts[product.id] ?? "");
  const isSelected =
    recurringProductId === product.id ||
    (product.paymentOption === "one-time" &&
      ((product.pricingMode === "fixed" && (quantities[product.id] ?? 0) > 0) ||
        (product.pricingMode === "custom" &&
          customAmount !== null &&
          customAmount >= getLockerverseMinimumCustomAmountCents(product))));
  return (
    <article
      className={`lockerverse-launcher__product lockerverse-launcher__product--${variant}`}
      data-selected={isSelected || undefined}
    >
      <span className="lockerverse-launcher__product-copy">
        <strong className="lockerverse-launcher__product-name">
          {product.name}
        </strong>
        {product.description ? <small>{product.description}</small> : null}
        {product.amountCents === null ? null : (
          <span className="lockerverse-launcher__product-price">
            {formatProductPrice(product.amountCents, product.paymentOption)}
          </span>
        )}
      </span>
      <LauncherProductControl {...props} />
    </article>
  );
}

function LauncherDonationOption({
  customAmounts,
  product,
  setCustomAmounts,
  setRecurringProductId,
}: LauncherProductControlProps) {
  const amount = customAmounts[product.id] ?? "";
  const amountCents = parseAmountCents(amount);
  const suggestions = suggestedDonationAmountsCents.filter(
    (suggestion) =>
      suggestion >= getLockerverseMinimumCustomAmountCents(product)
  );
  const isSuggestedAmount = suggestions.includes(amountCents ?? 0);
  const setAmount = (value: string) => {
    setRecurringProductId(null);
    setCustomAmounts((current) => ({
      ...current,
      [product.id]: value,
    }));
  };

  return (
    <div className="lockerverse-launcher__donation-option">
      <fieldset className="lockerverse-launcher__donation-choices">
        <legend className="lockerverse-launcher__sr-only">
          {product.name} amount
        </legend>
        <button
          aria-pressed={amount === ""}
          onClick={() => setAmount("")}
          type="button"
        >
          No thanks
        </button>
        {suggestions.map((suggestion) => (
          <button
            aria-pressed={amountCents === suggestion}
            key={suggestion}
            onClick={() => setAmount(String(suggestion / 100))}
            type="button"
          >
            {usd.format(suggestion / 100).replace(".00", "")}
          </button>
        ))}
        <label
          className="lockerverse-launcher__donation-other"
          data-selected={amount !== "" && !isSuggestedAmount ? true : undefined}
        >
          <input
            aria-label={`${product.name} amount`}
            min={getLockerverseMinimumCustomAmountCents(product) / 100}
            name={`amount-${product.slug}`}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="Other $"
            step="0.01"
            type="number"
            value={isSuggestedAmount ? "" : amount}
          />
        </label>
      </fieldset>
    </div>
  );
}

type LauncherProductListProps = Omit<LauncherProductControlProps, "product"> & {
  products: LockerverseProduct[];
  variant?: LauncherProductOptionProps["variant"];
};

function LauncherProductList({
  products,
  variant = "default",
  ...controlProps
}: LauncherProductListProps) {
  return (
    <div className="lockerverse-launcher__product-list">
      {products.map((product) =>
        variant === "donation" ? (
          <LauncherDonationOption
            {...controlProps}
            key={product.id}
            product={product}
          />
        ) : (
          <LauncherProductOption
            {...controlProps}
            key={product.id}
            product={product}
            variant={variant}
          />
        )
      )}
    </div>
  );
}

function getSelectionSummary(
  catalog: LockerverseCatalog,
  selection: LockerversePaymentSelectionItem[]
) {
  let itemCount = 0;
  let amountCents = 0;
  for (const item of selection) {
    const product = catalog.products.find(
      (candidate) => candidate.slug === item.productSlug
    );
    if (!product) {
      continue;
    }
    if ("quantity" in item) {
      itemCount += item.quantity;
      amountCents += (product.amountCents ?? 0) * item.quantity;
      continue;
    }
    itemCount += 1;
    amountCents +=
      "amountCents" in item ? item.amountCents : (product.amountCents ?? 0);
  }
  return { amountCents, itemCount };
}

function LauncherLoading() {
  return (
    <div
      aria-label="Loading donation options"
      className="lockerverse-launcher__loading"
      role="status"
    >
      {[0, 1, 2].map((item) => (
        <span
          aria-hidden="true"
          className="lockerverse-launcher__loading-row"
          key={item}
        />
      ))}
    </div>
  );
}

function createLauncherSelection(
  catalog: LockerverseCatalog,
  quantities: Record<string, number>,
  customAmounts: Record<string, string>,
  recurringProductId: string | null
): LockerversePaymentSelectionItem[] {
  if (recurringProductId) {
    const product = catalog.products.find(
      (candidate) =>
        candidate.id === recurringProductId &&
        candidate.paymentOption !== "one-time"
    );
    return product ? [{ productSlug: product.slug }] : [];
  }

  return catalog.products.flatMap(
    (product): LockerversePaymentSelectionItem[] => {
      const quantity = quantities[product.id] ?? 0;
      if (
        product.pricingMode === "fixed" &&
        product.paymentOption === "one-time" &&
        quantity > 0
      ) {
        return [{ productSlug: product.slug, quantity }];
      }
      const amountCents = parseAmountCents(customAmounts[product.id] ?? "");
      if (
        product.pricingMode === "custom" &&
        product.paymentOption === "one-time" &&
        amountCents !== null &&
        amountCents >= getLockerverseMinimumCustomAmountCents(product)
      ) {
        return [{ amountCents, productSlug: product.slug }];
      }
      return [];
    }
  );
}

type LauncherSelectionStateProps = Omit<LauncherProductControlProps, "product">;

type LauncherCatalogProps = LauncherSelectionStateProps & {
  catalog: LockerverseCatalog;
  onContinue: (selection: LockerversePaymentSelectionItem[]) => void;
};

function LauncherCatalog({
  catalog,
  onContinue,
  ...selectionState
}: LauncherCatalogProps) {
  const draftSelection = createLauncherSelection(
    catalog,
    selectionState.quantities,
    selectionState.customAmounts,
    selectionState.recurringProductId
  );
  const summary = getSelectionSummary(catalog, draftSelection);
  const donationProducts = catalog.products.filter(
    (product) =>
      product.paymentOption === "one-time" && product.pricingMode === "custom"
  );
  const orderProducts = catalog.products.filter(
    (product) => !donationProducts.includes(product)
  );

  return (
    <form
      className="lockerverse-launcher__products"
      onSubmit={(event) => {
        event.preventDefault();
        if (draftSelection.length > 0) {
          onContinue(draftSelection);
        }
      }}
    >
      {orderProducts.length > 0 ? (
        <section className="lockerverse-launcher__section">
          <LauncherProductList {...selectionState} products={orderProducts} />
        </section>
      ) : null}
      {donationProducts.length > 0 ? (
        <section className="lockerverse-launcher__donation">
          <header className="lockerverse-launcher__section-header">
            <h3>Add a donation</h3>
            <p>Choose an amount to add to this order.</p>
          </header>
          <LauncherProductList
            {...selectionState}
            products={donationProducts}
            variant="donation"
          />
        </section>
      ) : null}
      {catalog.products.length > 0 ? (
        <footer className="lockerverse-launcher__footer">
          <p aria-live="polite" className="lockerverse-launcher__summary">
            <span>
              {summary.itemCount === 0
                ? "No items selected"
                : `${summary.itemCount} ${summary.itemCount === 1 ? "item" : "items"} selected`}
            </span>
            {summary.itemCount === 0 ? null : (
              <strong>{usd.format(summary.amountCents / 100)}</strong>
            )}
          </p>
          <button
            className="lockerverse-launcher__continue"
            disabled={draftSelection.length === 0}
            type="submit"
          >
            Continue
          </button>
        </footer>
      ) : null}
    </form>
  );
}

type LauncherPaymentProps = Omit<
  LockerversePaymentProps,
  "selection" | "widget"
>;

type LauncherCheckoutProps = {
  onEdit: () => void;
  paymentProps: LauncherPaymentProps;
  selection: LockerversePaymentSelectionItem[];
  style: LockerversePaymentStyle;
  widget: UseLockerverseWidgetResult;
};

function LauncherCheckout({
  onEdit,
  paymentProps,
  selection,
  style,
  widget,
}: LauncherCheckoutProps) {
  return (
    <div className="lockerverse-launcher__checkout">
      <header className="lockerverse-launcher__checkout-toolbar">
        <button
          className="lockerverse-launcher__back"
          onClick={onEdit}
          type="button"
        >
          Edit order
        </button>
      </header>
      <LockerversePayment
        {...paymentProps}
        selection={selection}
        style={style}
        widget={widget}
      />
    </div>
  );
}

function LauncherDialogHeading({
  catalog,
  checkout,
}: {
  catalog: LockerverseCatalog | null;
  checkout: boolean;
}) {
  if (checkout) {
    return (
      <>
        <Dialog.Title className="lockerverse-launcher__sr-only">
          Checkout
        </Dialog.Title>
        <Dialog.Description className="lockerverse-launcher__sr-only">
          Review the order and complete payment.
        </Dialog.Description>
      </>
    );
  }

  if (!catalog) {
    return (
      <header className="lockerverse-launcher__header">
        <Dialog.Title className="lockerverse-launcher__title">
          Donate
        </Dialog.Title>
        <Dialog.Description className="lockerverse-launcher__description">
          Choose your options, then continue to checkout.
        </Dialog.Description>
      </header>
    );
  }

  return (
    <header className="lockerverse-launcher__header">
      {catalog.community ? (
        <div className="lockerverse-launcher__brand">
          {catalog.community.imageUrl ? (
            <img
              alt={`${catalog.community.name} logo`}
              height="28"
              src={catalog.community.imageUrl}
              width="28"
            />
          ) : null}
          <span>{catalog.community.name}</span>
        </div>
      ) : null}
      <Dialog.Title className="lockerverse-launcher__title">
        {catalog.widget.title ?? catalog.widget.name}
      </Dialog.Title>
      <Dialog.Description className="lockerverse-launcher__description">
        {catalog.widget.description ??
          "Choose your options, then continue to checkout."}
      </Dialog.Description>
    </header>
  );
}

export function LockerverseDonateLauncher({
  buttonLabel = "Donate",
  position = "inline",
  widget,
  ...paymentProps
}: LockerverseDonateLauncherProps) {
  const [selection, setSelection] = useState<
    LockerversePaymentSelectionItem[] | null
  >(null);
  const [customAmounts, setCustomAmounts] = useState<Record<string, string>>(
    {}
  );
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [recurringProductId, setRecurringProductId] = useState<string | null>(
    null
  );
  const { catalog, error, loading } = widget;
  const accent = normalizeAccentColor(
    paymentProps.style?.["--lockerverse-accent"] ??
      catalog?.community?.accentColor ??
      "#38d77a"
  );
  const accentContrast = getAccentContrast(accent);
  const launcherStyle: LockerversePaymentStyle = { ...paymentProps.style };
  if (
    paymentProps.style?.["--lockerverse-accent"] !== undefined ||
    catalog?.community?.accentColor
  ) {
    launcherStyle["--lockerverse-accent"] = accent;
  }
  if (
    accentContrast &&
    paymentProps.style?.["--lockerverse-accent-contrast"] === undefined
  ) {
    launcherStyle["--lockerverse-accent-contrast"] = accentContrast;
  }

  let content: ReactNode;
  if (loading) {
    content = <LauncherLoading />;
  } else if (error) {
    content = (
      <div role="alert">
        <p>Unable to load donation options.</p>
        <button onClick={widget.refresh} type="button">
          Retry
        </button>
      </div>
    );
  } else if (catalog) {
    content = (
      <LauncherCatalog
        catalog={catalog}
        customAmounts={customAmounts}
        onContinue={setSelection}
        quantities={quantities}
        recurringProductId={recurringProductId}
        setCustomAmounts={setCustomAmounts}
        setQuantities={setQuantities}
        setRecurringProductId={setRecurringProductId}
      />
    );
  } else {
    content = <LauncherLoading />;
  }
  if (!(loading || error) && selection && catalog) {
    content = (
      <LauncherCheckout
        onEdit={() => setSelection(null)}
        paymentProps={paymentProps}
        selection={selection}
        style={launcherStyle}
        widget={widget}
      />
    );
  }

  return (
    <Dialog.Root
      onOpenChange={(open) => {
        if (open && catalog && paymentProps.stripeLoader === undefined) {
          requestAnimationFrame(() => {
            preloadLockerversePayment(catalog);
          });
        }
      }}
    >
      <Dialog.Trigger
        className={`lockerverse-launcher lockerverse-launcher--${position}`}
        data-lockerverse-theme={paymentProps.theme ?? "dark"}
        style={launcherStyle}
      >
        {buttonLabel}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop className="lockerverse-launcher__backdrop" />
        <Dialog.Viewport className="lockerverse-launcher__viewport">
          <Dialog.Popup
            className="lockerverse-launcher__dialog"
            data-lockerverse-theme={paymentProps.theme ?? "dark"}
            style={launcherStyle}
          >
            <LauncherDialogHeading
              catalog={catalog}
              checkout={selection !== null}
            />
            <Dialog.Close
              aria-label="Close donation dialog"
              className="lockerverse-launcher__close"
            >
              <span aria-hidden="true">×</span>
            </Dialog.Close>
            {content}
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
